import WisdomNavbar from "components/navbars/WisdomNabvar";
import { useAuth } from "context/AuthContext"
import { useLanguage } from "context/LanguageContext";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { fetchEmployeeDetailsData, updateEmployeeDetailsData } from "services/data";
import { fetchWisdomRelatedEmployees, fetchWisdomRelatedSchools } from "services/wisdom/wisdomData";

const EMPTY_FORM = {
    first_name: "",
    middle_name: "",
    last_name: "",
    birth_date: "",
    address: "",
    qualification: "",
    email: "",
    id_number: "",
    birth_place: "",
    sex: "",
    religion: "",
    phone_number: "",
    financial_job_level: "",
    qualitative_group: "",
    appointment_date: "",
    date_of_receipt_of_current_work: "",
    contract_type: "",
};

const DATE_FIELDS = ["birth_date", "appointment_date", "date_of_receipt_of_current_work"];

const toDateInputValue = (value) => (value ? String(value).slice(0, 10) : "");

const FIELDS = [
    { key: "first_name", label: { en: "First Name", ar: "الاسم الأول" }, type: "text", required: true },
    { key: "middle_name", label: { en: "Middle Name", ar: "الاسم الأوسط" }, type: "text" },
    { key: "last_name", label: { en: "Last Name", ar: "اسم العائلة" }, type: "text", required: true },
    { key: "email", label: { en: "Email", ar: "البريد الإلكتروني" }, type: "email" },
    { key: "id_number", label: { en: "National ID", ar: "الرقم القومي" }, type: "text" },
    { key: "birth_date", label: { en: "Birth Date", ar: "تاريخ الميلاد" }, type: "date" },
    { key: "birth_place", label: { en: "Birth Place", ar: "محل الميلاد" }, type: "text" },
    { key: "sex", label: { en: "Sex", ar: "النوع" }, type: "select", options: [
        { value: "male", label: { en: "Male", ar: "ذكر" } },
        { value: "female", label: { en: "Female", ar: "انثي" } },
    ] },
    { key: "religion", label: { en: "Religion", ar: "الديانة" }, type: "text" },
    { key: "address", label: { en: "Address", ar: "العنوان الحالي" }, type: "text" },
    { key: "qualification", label: { en: "Qualification", ar: "المؤهل الدراسي" }, type: "text" },
    { key: "phone_number", label: { en: "Phone Number", ar: "رقم الهاتف المحمول" }, type: "tel" },
    { key: "financial_job_level", label: { en: "Financial Job Level", ar: "الدرجة الوظيفية المالية" }, type: "text" },
    { key: "qualitative_group", label: { en: "Qualitative Group", ar: "المجموعة النوعية" }, type: "text" },
    { key: "appointment_date", label: { en: "Appointment Date", ar: "تاريخ التعيين الفعلي" }, type: "date" },
    { key: "date_of_receipt_of_current_work", label: { en: "Date of Receipt of Current Work", ar: "تاريخ استلام العمل الحالي" }, type: "date" },
    { key: "contract_type", label: { en: "Contract Type", ar: "نوع التعاقد" }, type: "text" },
];

const WisdomAdminEmployeeData = () => {
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const isMultiSchoolAdmin = userInfo?.organization_id === 13;

    const [schools, setSchools] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);

    useEffect(() => {
        const loadData = async () => {
            const [schoolResponse, employeesResponse] = await Promise.all([
                fetchWisdomRelatedSchools(),
                fetchWisdomRelatedEmployees(),
            ]);
            setSchools(schoolResponse);
            setEmployees(employeesResponse);
        };

        loadData();
    }, []);

    const effectiveSchoolId = isMultiSchoolAdmin ? selectedSchool : userInfo?.organization_id;

    const filteredEmployees = useMemo(
        () =>
            employees.filter((emp) =>
                effectiveSchoolId === "" || effectiveSchoolId == null
                    ? true
                    : emp?.employee?.organization_id === Number(effectiveSchoolId)
            ),
        [employees, effectiveSchoolId]
    );

    useEffect(() => {
        if (!selectedEmployee) {
            setFormData(EMPTY_FORM);
            return;
        }

        const loadEmployeeDetails = async () => {
            setLoadingDetails(true);
            try {
                const details = await fetchEmployeeDetailsData(selectedEmployee);
                const nextForm = { ...EMPTY_FORM };
                Object.keys(EMPTY_FORM).forEach((key) => {
                    const value = details?.[key];
                    nextForm[key] = DATE_FIELDS.includes(key) ? toDateInputValue(value) : value ?? "";
                });
                setFormData(nextForm);
            } catch {
                toast.error(language ? "Failed to load employee details" : "فشل تحميل بيانات الموظف");
                setFormData(EMPTY_FORM);
            } finally {
                setLoadingDetails(false);
            }
        };

        loadEmployeeDetails();
    }, [selectedEmployee, language]);

    const handleFieldChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedEmployee) return;

        setSubmitting(true);
        try {
            await updateEmployeeDetailsData(selectedEmployee, formData);
            toast.success(language ? "Employee data saved successfully" : "تم حفظ بيانات الموظف بنجاح");
        } catch {
            toast.error(language ? "Failed to save employee data" : "فشل حفظ بيانات الموظف");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <WisdomNavbar />
            <div className="max-w-3xl mx-auto px-4 py-8" dir={language ? "ltr" : "rtl"}>
                <div className="bg-white rounded-2xl shadow p-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-6">
                        {language ? "Employee Personal Data" : "بيانات الموظفين الشخصية"}
                    </h1>

                    <div className="space-y-5 mb-6">
                        {isMultiSchoolAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language ? "School" : "مدرسة"}
                                </label>
                                <select
                                    value={selectedSchool}
                                    onChange={(e) => {
                                        setSelectedSchool(e.target.value);
                                        setSelectedEmployee("");
                                    }}
                                    className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                >
                                    <option value="">
                                        {language ? "All schools" : "كل المدارس"}
                                    </option>
                                    {schools.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language ? "Employee" : "الموظف"}
                            </label>
                            <select
                                value={selectedEmployee}
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                                className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                            >
                                <option value="">
                                    {language ? "Select employee" : "اختر موظف"}
                                </option>
                                {filteredEmployees.map((emp) => (
                                    <option key={emp.employee.id} value={emp.employee.id}>
                                        {emp?.employee?.first_name} {emp?.employee?.middle_name} {emp?.employee?.last_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedEmployee && (
                        loadingDetails ? (
                            <p className="text-sm text-gray-500">
                                {language ? "Loading employee data..." : "جاري تحميل بيانات الموظف..."}
                            </p>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    {FIELDS.map((field) => (
                                        <div key={field.key}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {language ? field.label.en : field.label.ar}
                                            </label>
                                            {field.type === "select" ? (
                                                <select
                                                    value={formData[field.key]}
                                                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                                    className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                    required={field.required}
                                                >
                                                    <option value="">
                                                        {language ? "Select" : "اختر"}
                                                    </option>
                                                    {field.options.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {language ? opt.label.en : opt.label.ar}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type={field.type}
                                                    value={formData[field.key]}
                                                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                                    className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                    required={field.required}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="submit"
                                    className="w-full py-2 rounded-xl bg-watomsBlue text-white hover:opacity-90 transition disabled:opacity-50"
                                    disabled={submitting}
                                >
                                    {submitting
                                        ? (language ? "Saving..." : "جاري الحفظ...")
                                        : (language ? "Save" : "حفظ")}
                                </button>
                            </form>
                        )
                    )}
                </div>
            </div>
        </>
    )
};

export default WisdomAdminEmployeeData;